import { useCallback, useRef } from 'react';
import { create } from 'zustand';

import type { I18nLocale } from '#/i18n/types/I18nLocale';

import { setGMapsError } from './gMapsErrorStore';

type GoogleMapsAutocompleteStoreLibraries = {
  geocoder: google.maps.Geocoder;
  suggestion: typeof google.maps.places.AutocompleteSuggestion;
  sessionToken: google.maps.places.AutocompleteSessionToken;
};

let librariesPromise:
  | Promise<GoogleMapsAutocompleteStoreLibraries | null>
  | undefined;

const googleMapsLibrariesStore = create<{
  libraries: GoogleMapsAutocompleteStoreLibraries | null;
}>(() => ({ libraries: null }));

// const useGoogleMapsLibrariesStore = googleMapsLibrariesStore; // The store can be used as a hook

async function internalLoadGoogleMapsLibraries() {
  try {
    const [{ Geocoder }, { AutocompleteSuggestion, AutocompleteSessionToken }] =
      await Promise.all([
        google.maps.importLibrary('geocoding'),
        google.maps.importLibrary('places'),
      ]);

    const libraries: GoogleMapsAutocompleteStoreLibraries = {
      geocoder: new Geocoder(),
      suggestion: AutocompleteSuggestion,
      sessionToken: new AutocompleteSessionToken(),
    };

    googleMapsLibrariesStore.setState({ libraries });

    return libraries;
  } catch (error) {
    if (error instanceof Error) {
      setGMapsError(error.message);
    }

    return null;
  }
}

// Actions

export function loadGoogleMapsLibraries() {
  const { libraries } = googleMapsLibrariesStore.getState();
  if (libraries) return libraries;

  if (!librariesPromise) librariesPromise = internalLoadGoogleMapsLibraries();

  return librariesPromise;
}

type FetchGoogleMapsSuggestionsPayload = {
  input: string;
  includedRegionCodes?: string[];
  addressTypes?: string[];
};

export async function fetchGoogleMapsSuggestions({
  input,
  includedRegionCodes,
  addressTypes,
}: FetchGoogleMapsSuggestionsPayload): Promise<
  google.maps.places.AutocompleteSuggestion[]
> {
  const libraries = await loadGoogleMapsLibraries();
  if (!libraries) return [];

  const response = await libraries.suggestion.fetchAutocompleteSuggestions({
    input,
    includedRegionCodes,
    includedPrimaryTypes: addressTypes ?? ['geocode'],
    sessionToken: libraries.sessionToken,
  });

  return response.suggestions;
}

export async function fetchGoogleMapsPlaceDetails(payload: {
  placeId: string;
  language?: I18nLocale;
}): Promise<google.maps.GeocoderResult | null> {
  const { placeId, language } = payload;

  try {
    const libraries = await loadGoogleMapsLibraries();
    if (!libraries || !placeId) return null;

    const response = await libraries.geocoder.geocode({ placeId, language });

    if (response.results.length === 0) return null;

    return response.results[0];
  } catch (error) {
    if (error instanceof Error) setGMapsError(error.message);

    return null;
  }
}

export async function lookupGoogleMapsAddress(payload: {
  query: string;
  country?: string;
}): Promise<google.maps.GeocoderResult | null> {
  const { query, country } = payload;

  try {
    const libraries = await loadGoogleMapsLibraries();
    if (!libraries) return null;

    const response = await libraries.geocoder.geocode({
      address: query,
      componentRestrictions: { country },
    });

    if (response.results.length === 0) return null;

    return response.results[0];
  } catch (error) {
    if (error instanceof Error) setGMapsError(error.message);

    return null;
  }
}

// Hooks

/**
 * A wrapper for `fetchGoogleMapsSuggestions` with debouncing and abortion control
 */
type GoogleMapsAutocompleteResponse =
  | { type: 'aborted' }
  | {
      type: 'results';
      suggestions: google.maps.places.AutocompleteSuggestion[];
    };

export function useGoogleMapsAutocomplete() {
  const latestAbortControllerRef = useRef<AbortController>(null);
  const abortableGoogleMapsAutocomplete = useCallback(
    async (
      payload: FetchGoogleMapsSuggestionsPayload,
      abortSignal: AbortSignal,
    ): Promise<GoogleMapsAutocompleteResponse> => {
      void loadGoogleMapsLibraries(); // eagerly load library - it will only load once
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 300);
      });

      if (abortSignal.aborted) return { type: 'aborted' };

      const suggestions = await fetchGoogleMapsSuggestions(payload);

      if (abortSignal.aborted || !suggestions) return { type: 'aborted' };

      return { type: 'results', suggestions };
    },
    [],
  );

  const googleMapsAutocomplete = useCallback(
    (payload: FetchGoogleMapsSuggestionsPayload) => {
      latestAbortControllerRef.current?.abort();

      const abortController = new AbortController();
      latestAbortControllerRef.current = abortController;

      return new Promise<GoogleMapsAutocompleteResponse>((resolve) => {
        let resolved = false;

        const abortListener = () => {
          if (resolved) return;

          resolve({ type: 'aborted' });
          resolved = true;
          abortController.signal.removeEventListener('abort', abortListener);
        };

        abortController.signal.addEventListener('abort', abortListener);

        void abortableGoogleMapsAutocomplete(
          payload,
          abortController.signal,
        ).then((result) => {
          if (resolved) return;

          resolve(result);
          resolved = true;
          abortController.signal.removeEventListener('abort', abortListener);

          return undefined;
        });
      });
    },
    [abortableGoogleMapsAutocomplete],
  );

  const abortGoogleMapsAutocomplete = useCallback(() => {
    latestAbortControllerRef.current?.abort();
  }, []);

  return { googleMapsAutocomplete, abortGoogleMapsAutocomplete };
}
